import { useState } from 'react';
import { DiagnosticsFooter, EmptyState, usePayload } from './primitives';
import styles from './styles.module.css';
import s from './ProductCatalogViewer.module.css';

const FRESHNESS_CLASS = { High: s.freshnessHigh, Medium: s.freshnessMedium, Low: s.freshnessLow };
const PRODUCT_PREVIEW = 12;

const nonNegativeCount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  return Math.max(0, number);
};

const ProductCatalogViewer = ({ report }) => {
  const payload = usePayload(report);
  const [showAllProducts, setShowAllProducts] = useState(false);

  if (!report) return <EmptyState />;

  const fields = Array.isArray(payload?.fields) ? payload.fields : [];
  const byKey = Object.fromEntries(fields.map((f) => [f.key, f]));

  const totalProducts      = nonNegativeCount(byKey['total_active_products']?.value);
  const numberOfCollections = nonNegativeCount(byKey['number_of_collections']?.value);
  const assortmentDepth    = byKey['assortment_depth']?.value || null;
  const assortmentFocus    = byKey['assortment_focus']?.value || null;
  const catalogFreshness   = byKey['catalog_freshness']?.value || null;
  const totalInventoryUnits = nonNegativeCount(byKey['total_inventory_units']?.value);
  const lowStockCount      = nonNegativeCount(byKey['low_stock_products']?.value);
  const campaignBackedCount = nonNegativeCount(byKey['campaign_backed_products']?.value);
  const activeCampaignBackedCount = nonNegativeCount(byKey['active_campaign_backed_products']?.value);
  const dominantFamilies   = Array.isArray(byKey['dominant_product_families']?.value) ? byKey['dominant_product_families'].value : [];
  const productTypeSummary = Array.isArray(byKey['product_type_summary']?.value) ? byKey['product_type_summary'].value : [];
  const activeCollections  = Array.isArray(byKey['active_collection_set']?.value) ? byKey['active_collection_set'].value : [];
  const allProducts        = Array.isArray(byKey['active_sellable_product_set']?.value) ? byKey['active_sellable_product_set'].value : [];

  const dominantSet = new Set(dominantFamilies);
  const secondaryFamilies = productTypeSummary.filter((f) => !dominantSet.has(f));
  const visibleProducts = showAllProducts ? allProducts : allProducts.slice(0, PRODUCT_PREVIEW);

  const missing = Array.isArray(payload?.diagnostics?.missing_data_flags) ? payload.diagnostics.missing_data_flags : [];

  return (
    <div className={styles.viewer}>

      {/* Stats strip */}
      <div className={s.statsStrip}>
        {totalProducts !== null && (
          <div className={s.statPill}>
            <p className={s.statNumber}>{totalProducts}</p>
            <p className={s.statLabel}>Active Products</p>
          </div>
        )}
        {numberOfCollections !== null && (
          <div className={s.statPill}>
            <p className={s.statNumber}>{numberOfCollections}</p>
            <p className={s.statLabel}>Collections</p>
          </div>
        )}
        {totalInventoryUnits !== null && (
          <div className={s.statPill}>
            <p className={s.statNumber}>{totalInventoryUnits}</p>
            <p className={s.statLabel}>Stock Units</p>
          </div>
        )}
        {lowStockCount !== null && (
          <div className={`${s.statPill} ${lowStockCount > 0 ? s.statPillAlert : ''}`}>
            <p className={s.statNumber}>{lowStockCount}</p>
            <p className={s.statLabel}>Low Stock</p>
          </div>
        )}
        {campaignBackedCount !== null && (
          <div className={s.statPill}>
            <p className={s.statNumber}>{campaignBackedCount}</p>
            <p className={s.statLabel}>In Meta Ads</p>
          </div>
        )}
        {activeCampaignBackedCount !== null && (
          <div className={s.statPill}>
            <p className={s.statNumber}>{activeCampaignBackedCount}</p>
            <p className={s.statLabel}>Active Campaigns</p>
          </div>
        )}
      </div>

      {/* Catalog profile */}
      {(assortmentDepth || assortmentFocus || catalogFreshness) && (
        <div className={s.profileBlock}>
          <div className={s.blockHeader}>
            <span className={s.blockTitle}>Catalog Profile</span>
          </div>
          <div className={s.profileRow}>
            {assortmentDepth && (
              <div className={s.profileAttr}>
                <span className={s.profileAttrLabel}>Depth</span>
                <span className={s.profileAttrValue}>{assortmentDepth}</span>
              </div>
            )}
            {assortmentDepth && assortmentFocus && <span className={s.profileDivider} />}
            {assortmentFocus && (
              <div className={s.profileAttr}>
                <span className={s.profileAttrLabel}>Focus</span>
                <span className={s.profileAttrValue}>{assortmentFocus}</span>
              </div>
            )}
            {assortmentFocus && catalogFreshness && <span className={s.profileDivider} />}
            {catalogFreshness && (
              <div className={s.profileAttr}>
                <span className={s.profileAttrLabel}>Freshness</span>
                <span className={`${s.profileAttrValue} ${FRESHNESS_CLASS[catalogFreshness] || ''}`}>
                  {catalogFreshness}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Product families */}
      {(dominantFamilies.length > 0 || secondaryFamilies.length > 0) && (
        <div className={s.familiesBlock}>
          <div className={s.blockHeader}>
            <span className={s.blockTitle}>Product Families</span>
            {productTypeSummary.length > 0 && (
              <span className={s.blockBadge}>{productTypeSummary.length} types</span>
            )}
          </div>
          <div className={s.chipsArea}>
            {dominantFamilies.map((f, i) => (
              <span key={i} className={s.familyChipPrimary}>{f}</span>
            ))}
            {secondaryFamilies.map((f, i) => (
              <span key={i} className={s.familyChip}>{f}</span>
            ))}
          </div>
        </div>
      )}

      {/* Collections */}
      {activeCollections.length > 0 && (
        <div className={s.collectionsBlock}>
          <div className={s.blockHeader}>
            <span className={s.blockTitle}>Collections</span>
            <span className={s.blockBadge}>{activeCollections.length}</span>
          </div>
          <div className={s.chipsArea}>
            {activeCollections.map((c, i) => (
              <span key={i} className={s.collectionChip}>{c}</span>
            ))}
          </div>
        </div>
      )}

      {/* Active product set */}
      {allProducts.length > 0 && (
        <div className={s.productListBlock}>
          <div className={s.blockHeader}>
            <span className={s.blockTitle}>Active Product Set</span>
            <span className={s.blockBadge}>{allProducts.length} products</span>
          </div>
          <ul className={s.productList}>
            {visibleProducts.map((product, i) => {
              const isRichProduct = product && typeof product === 'object';
              const title = isRichProduct ? product.title : product;
              const stock = isRichProduct ? nonNegativeCount(product.total_inventory) : null;
              const variants = isRichProduct && Array.isArray(product.variants) ? product.variants : [];
              const lowVariantCount = variants.filter((variant) => variant.low_stock).length;
              const lowStock = Boolean(isRichProduct && product.low_stock);
              const campaignActivity = isRichProduct && Array.isArray(product.campaign_activity)
                ? product.campaign_activity
                : [];
              const visibleCampaignActivity = campaignActivity.slice(0, 3);

              return (
                <li key={`${title || 'product'}-${i}`} className={`${s.productItem} ${lowStock ? s.productItemAlert : ''}`}>
                  <span className={s.productIndex}>{i + 1}</span>
                  <div className={s.productMain}>
                    <span className={s.productTitle}>{title}</span>
                    {campaignActivity.length > 0 && (
                      <div className={s.campaignBlock}>
                        {visibleCampaignActivity.map((activity, activityIndex) => {
                          const campaignActiveLabel = activity?.campaign_active_label || 'unknown';
                          const campaignStatusLabel = campaignActiveLabel === 'active'
                            ? 'Active'
                            : campaignActiveLabel === 'not_active'
                              ? 'Not active'
                              : 'Unknown';

                          return (
                            <div
                              key={`${activity?.ad_id || activity?.ad_name || activity?.destination_url || 'meta'}-${activityIndex}`}
                              className={s.campaignItem}
                            >
                              <div className={s.campaignMetaRow}>
                                <span className={s.campaignMetaLabel}>Campaign</span>
                                <span className={s.campaignMetaValue}>{activity?.campaign_name || 'Meta campaign'}</span>
                                <span className={`${s.campaignStatus} ${
                                  campaignActiveLabel === 'active'
                                    ? s.campaignStatusActive
                                    : campaignActiveLabel === 'not_active'
                                      ? s.campaignStatusInactive
                                      : s.campaignStatusUnknown
                                }`}>
                                  {campaignStatusLabel}
                                </span>
                              </div>
                              <div className={s.campaignMetaRow}>
                                <span className={s.campaignMetaLabel}>Dates</span>
                                <span className={s.campaignMetaValue}>
                                  {activity?.campaign_start_date || 'Unknown start'} - {activity?.campaign_end_date || 'No end date'}
                                </span>
                              </div>
                              <div className={s.campaignMetaRow}>
                                <span className={s.campaignMetaLabel}>Ad set</span>
                                <span className={s.campaignMetaValue}>{activity?.adset_name || 'Unknown ad set'}</span>
                              </div>
                              <div className={s.campaignMetaRow}>
                                <span className={s.campaignMetaLabel}>Ad</span>
                                <span className={s.campaignMetaValue}>{activity?.ad_name || 'Unknown ad'}</span>
                              </div>
                              {activity?.destination_url && (
                                <div className={s.campaignMetaRow}>
                                  <span className={s.campaignMetaLabel}>Product link</span>
                                  <a
                                    className={s.campaignProductLink}
                                    href={activity.destination_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {activity.destination_url}
                                  </a>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {campaignActivity.length > visibleCampaignActivity.length && (
                          <span className={s.campaignMore}>
                            +{campaignActivity.length - visibleCampaignActivity.length} more matched ads
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {stock !== null && stock !== undefined && (
                    <span className={`${s.stockBadge} ${lowStock ? s.stockBadgeAlert : ''}`}>
                      {stock} total
                    </span>
                  )}
                  {variants.length > 0 && (
                    <div className={s.variantList}>
                      {lowVariantCount > 0 && (
                        <span className={s.variantAlertSummary}>
                          {lowVariantCount} variant{lowVariantCount === 1 ? '' : 's'} at or below 10 units
                        </span>
                      )}
                      {variants.map((variant, variantIndex) => (
                        <div
                          key={variant.shopify_variant_id || variant.sku || variantIndex}
                          className={`${s.variantItem} ${variant.low_stock ? s.variantItemAlert : ''}`}
                        >
                          <span className={s.variantSku}>{variant.sku || `Variant ${variantIndex + 1}`}</span>
                          <span className={s.variantStock}>
                            {nonNegativeCount(variant.inventory_quantity) ?? 0} units
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {allProducts.length > PRODUCT_PREVIEW && (
            <button className={s.showMoreBtn} onClick={() => setShowAllProducts((v) => !v)}>
              {showAllProducts ? 'Show less' : `Show all ${allProducts.length} products`}
            </button>
          )}
        </div>
      )}

      <DiagnosticsFooter missingFlags={missing} />
    </div>
  );
};

export default ProductCatalogViewer;
